import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Home, FolderKanban, Database, Share2, GitBranch,
  ClipboardCheck, Plug, Activity, Settings, Plus, Command,
  ArrowRight, Keyboard, X,
} from "lucide-react";

const PAGES = [
  { label: "Home", path: "/", icon: Home, section: "Navigate" },
  { label: "Projects", path: "/projects", icon: FolderKanban, section: "Navigate" },
  { label: "Repository", path: "/repository", icon: Database, section: "Navigate" },
  { label: "Knowledge Graph", path: "/graph", icon: Share2, section: "Navigate" },
  { label: "Review", path: "/review", icon: ClipboardCheck, section: "Navigate" },
  { label: "Orchestrations", path: "/orchestrations", icon: GitBranch, section: "Administration" },
  { label: "Agents", path: "/agents", icon: Search, section: "Administration" },
  { label: "Integrations", path: "/integrations", icon: Plug, section: "Administration" },
  { label: "Observability", path: "/observability", icon: Activity, section: "Administration" },
  { label: "Settings", path: "/settings", icon: Settings, section: "Administration" },
];

const ACTIONS = [
  { label: "Create New Project", action: "new-project", icon: Plus, section: "Actions" },
  { label: "Keyboard Shortcuts", action: "shortcuts", icon: Keyboard, section: "Actions" },
];

export const CommandPalette = ({ onClose, onAction }) => {
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const allItems = [...PAGES, ...ACTIONS];
  const filtered = query
    ? allItems?.filter(item => item.label.toLowerCase().includes(query.toLowerCase()))
    : allItems;

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { setSelectedIdx(0); }, [query]);

  const execute = useCallback((item) => {
    if (item.path) {
      navigate(item.path);
    } else if (item.action) {
      onAction?.(item.action);
    }
    onClose();
  }, [navigate, onClose, onAction]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, filtered.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    if (e.key === "Enter" && filtered[selectedIdx]) { execute(filtered[selectedIdx]); }
  };

  // Scroll selected item into view
  useEffect(() => {
    const el = listRef.current?.children[selectedIdx];
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedIdx]);

  // Group by section
  const sections = {};
  filtered.forEach((item, i) => {
    if (!sections[item.section]) sections[item.section] = [];
    sections[item.section].push({ ...item, globalIdx: i });
  });

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-[15vh] bg-black/25 backdrop-blur-sm" onClick={onClose} data-testid="command-palette-overlay">
      <div
        className="bg-white rounded-2xl shadow-elevated border border-border/60 w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="command-palette"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border/40">
          <Search className="w-4 h-4 text-foreground/50 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, actions..."
            className="flex-1 text-sm outline-none bg-transparent placeholder:text-foreground/45"
            data-testid="command-palette-input"
          />
          <kbd className="text-[10px] font-semibold text-foreground/50 border border-border/60 rounded px-1.5 py-0.5">ESC</kbd>
        </div>

        {/* Results */}
        <div className="max-h-72 overflow-y-auto py-2" ref={listRef}>
          {filtered.length === 0 ? (
            <p className="text-sm text-foreground/50 text-center py-6">No results found</p>
          ) : (
            Object.entries(sections)?.map(([section, items]) => (
              <div key={section}>
                <p className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider px-4 py-1.5">{section}</p>
                {items?.map((item) => (
                  <button
                    key={item.label}
                    onClick={() => execute(item)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${
                      item.globalIdx === selectedIdx ? "bg-[#014d7d]/5 text-[#014d7d]" : "text-foreground/70 hover:bg-foreground/[0.02]"
                    }`}
                    data-testid={`cmd-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon className="w-4 h-4 shrink-0 opacity-50" strokeWidth={1.7} />
                    <span className="text-sm flex-1">{item.label}</span>
                    <ArrowRight className="w-3 h-3 opacity-25" />
                  </button>
                ))}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-4 px-4 py-2.5 border-t border-border/40 bg-[#f8f9fb]">
          <span className="text-[10px] text-foreground/50 flex items-center gap-1">
            <kbd className="border border-border/60 rounded px-1 py-0.5 text-[9px] font-semibold">↑↓</kbd> Navigate
          </span>
          <span className="text-[10px] text-foreground/50 flex items-center gap-1">
            <kbd className="border border-border/60 rounded px-1 py-0.5 text-[9px] font-semibold">↵</kbd> Select
          </span>
          <span className="text-[10px] text-foreground/50 flex items-center gap-1">
            <kbd className="border border-border/60 rounded px-1 py-0.5 text-[9px] font-semibold">Esc</kbd> Close
          </span>
        </div>
      </div>
    </div>
  );
};

export const ShortcutsModal = ({ onClose }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/25 backdrop-blur-sm" onClick={onClose} data-testid="shortcuts-overlay">
    <div className="bg-white rounded-2xl shadow-elevated border border-border/60 w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="shortcuts-modal">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
        <h3 className="font-heading font-bold text-sm text-foreground flex items-center gap-2">
          <Keyboard className="w-4 h-4" /> Keyboard Shortcuts
        </h3>
        <button onClick={onClose} className="w-6 h-6 rounded-lg text-foreground/50 hover:text-foreground hover:bg-foreground/5 flex items-center justify-center" data-testid="close-shortcuts">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="px-5 py-4 space-y-3">
        {[
          { keys: ["Ctrl", "K"], desc: "Command palette" },
          { keys: ["Ctrl", "N"], desc: "New project" },
          { keys: ["Ctrl", "Enter"], desc: "Send message" },
          { keys: ["Ctrl", "/"], desc: "Show shortcuts" },
          { keys: ["Ctrl", "1-8"], desc: "Navigate to page" },
        ]?.map(({ keys, desc }) => (
          <div key={desc} className="flex items-center justify-between">
            <span className="text-xs text-foreground/60">{desc}</span>
            <div className="flex items-center gap-1">
              {keys?.map((k) => (
                <kbd key={k} className="text-[10px] font-semibold text-foreground/50 border border-border/60 rounded px-2 py-1 bg-[#f8f9fb] min-w-[24px] text-center">{k}</kbd>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);
